import React from 'react';
import { array, oneOf, string } from 'prop-types';
import {
  Accessibility,
  Armchair,
  Bed,
  CalendarClock,
  CalendarDays,
  Car,
  ChefHat,
  Landmark,
  MapPinned,
  Navigation,
  ScrollText,
  TableOfContents,
} from 'lucide-react';
import css from './SeoDiscovery.module.css';

const groupByPlacement = links => {
  const map = {};
  (links || []).forEach(link => {
    const key = link.placement || 'default';
    if (!map[key]) {
      map[key] = [];
    }
    map[key].push(link);
  });
  return map;
};

const GUIDE_LINK_META = {
  'seating-chart': { label: 'Seating Chart', Icon: Armchair },
  parking: { label: 'Parking', Icon: Car },
  'places-to-stay': { label: 'Places to Stay', Icon: Bed },
  faq: { label: 'FAQ', Icon: TableOfContents },
  restaurants: { label: 'Restaurants', Icon: ChefHat },
  'nearby-attractions': { label: 'Nearby Attractions', Icon: Landmark },
  directions: { label: 'Directions', Icon: Navigation },
  'past-events': { label: 'Past Events', Icon: CalendarClock },
  events: { label: 'Event Calendar', Icon: CalendarDays },
  accessibility: { label: 'Accessibility', Icon: Accessibility },
  rules: { label: 'Rules & Policies', Icon: ScrollText },
  neighborhood: { label: 'Neighborhood', Icon: MapPinned },
};

const guideSlugFromPath = targetPath =>
  String(targetPath || '')
    .split('/')
    .filter(Boolean)
    .pop();

const isVenueSubpagePath = targetPath => {
  const segments = String(targetPath || '')
    .split('/')
    .filter(Boolean);
  return segments[0] === 'event-discovery' && segments[1] === 'venues' && segments.length >= 4;
};

const guideMetaForLink = link => {
  const slug = guideSlugFromPath(link.targetPath);
  return GUIDE_LINK_META[slug] || { label: link.anchorText, Icon: MapPinned };
};

const isGuideLink = link => !!GUIDE_LINK_META[guideSlugFromPath(link.targetPath)];
const isRemovedGuideLink = link => isVenueSubpagePath(link.targetPath) && !isGuideLink(link);

const cityClusterDescription = ({ cityName, venueName, relatedVenues }) => {
  if (!cityName) {
    return null;
  }
  const otherVenues = (relatedVenues || [])
    .filter(venue => venue.venueName && venue.venueName !== venueName)
    .slice(0, 4)
    .map(venue => venue.venueName);
  const venuePhrase = otherVenues.length
    ? `including ${otherVenues.slice(0, 3).join(', ')}${otherVenues.length > 3 ? ', and more' : ''}`
    : 'with additional venues on the local calendar';
  return `${cityName} has more live events beyond ${venueName || 'this venue'}, ${venuePhrase}. Browse the city page for more upcoming shows, dates, and ticket options in the area.`;
};

const SeoInternalLinks = props => {
  const { links, relatedVenues, title, mode = 'all', venueName, cityName: cityNameProp } = props;
  const grouped = groupByPlacement(links);
  const guide = (grouped.venue_guide_nav || []).filter(isGuideLink);
  const siblingGuide = (grouped.sibling_nav || []).filter(isGuideLink);
  const contextualGuide = (grouped.contextual || []).filter(isGuideLink);
  const guideLinksByPath = new Map();
  [...guide, ...siblingGuide, ...contextualGuide].forEach(link => {
    if (!guideLinksByPath.has(link.targetPath)) {
      guideLinksByPath.set(link.targetPath, link);
    }
  });
  const guideLinks = Array.from(guideLinksByPath.values());
  const isNonGuideContextualLink = link =>
    !guideLinksByPath.has(link.targetPath) && !isRemovedGuideLink(link);
  const related = grouped.related_venues || [];
  const relatedCards = (relatedVenues || []).slice(0, 8);
  const cityLinks = grouped.city_cluster || [];
  const cityName = cityNameProp || cityLinks.find(link => link.cityName)?.cityName;
  const performerLinks = grouped.artists_at_venue || [];
  const contextual = [
    ...cityLinks,
    ...(grouped.category_cluster || []),
    ...(grouped.sibling_nav || []).filter(isNonGuideContextualLink),
    ...(grouped.contextual || []).filter(isNonGuideContextualLink),
  ];

  const performersList =
    performerLinks.length > 0 ? (
      <ul className={css.indexList}>
        {performerLinks.map(link => (
          <li key={link.linkId || link.targetPath}>
            <a href={link.targetPath}>{link.anchorText}</a>
          </li>
        ))}
      </ul>
    ) : null;

  if (mode === 'performers') {
    if (!performersList) {
      return null;
    }
    return (
      <div className={css.section}>
        <h2>Performers</h2>
        {performersList}
      </div>
    );
  }

  if (mode === 'guide') {
    if (!guideLinks.length) {
      return null;
    }
    return (
      <div className={css.section}>
        {title ? <h2>{title}</h2> : null}
        <nav className={css.navGrid} aria-label="Venue guide">
          {guideLinks.map(link => {
            const meta = guideMetaForLink(link);
            const GuideIcon = meta.Icon;
            return (
              <a
                key={link.linkId}
                className={css.guideLink}
                href={link.targetPath}
                title={link.anchorText}
                aria-label={link.anchorText}
              >
                <span className={css.guideIcon} aria-hidden="true">
                  <GuideIcon size={22} strokeWidth={2} />
                </span>
                <span>{meta.label}</span>
              </a>
            );
          })}
        </nav>
      </div>
    );
  }

  if (mode === 'city') {
    if (!contextual.length && !performersList) {
      return null;
    }
    const description = cityClusterDescription({ cityName, venueName, relatedVenues });
    return (
      <>
        {contextual.length ? (
          <div className={css.section}>
            <h2>{cityName ? `Explore more events in ${cityName}` : 'Explore more'}</h2>
            {description ? <p>{description}</p> : null}
            <ul>
              {contextual.map(link => (
                <li key={link.linkId}>
                  <a href={link.targetPath}>{link.anchorText}</a>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {performersList ? (
          <div className={css.section}>
            <h2>Performers</h2>
            {performersList}
          </div>
        ) : null}
      </>
    );
  }

  if (
    !guideLinks.length &&
    !related.length &&
    !relatedCards.length &&
    !contextual.length &&
    !performerLinks.length
  ) {
    return null;
  }

  return (
    <div className={css.section}>
      {title ? <h2>{title}</h2> : null}
      {guideLinks.length > 0 ? (
        <nav className={css.navGrid} aria-label="Venue guide">
          {guideLinks.map(link => {
            const meta = guideMetaForLink(link);
            const GuideIcon = meta.Icon;
            return (
              <a
                key={link.linkId}
                className={css.guideLink}
                href={link.targetPath}
                title={link.anchorText}
                aria-label={link.anchorText}
              >
                <span className={css.guideIcon} aria-hidden="true">
                  <GuideIcon size={22} strokeWidth={2} />
                </span>
                <span>{meta.label}</span>
              </a>
            );
          })}
        </nav>
      ) : null}
      {relatedCards.length > 0 ? (
        <div className={css.relatedVenues}>
          <h3>Related venues</h3>
          <div className={css.relatedVenueGrid}>
            {relatedCards.map(venue => (
              <a
                key={venue.venueId || venue.slug}
                className={css.relatedVenueCard}
                href={venue.canonicalPath || `/event-discovery/venues/${venue.slug}/`}
              >
                {venue.image?.url ? (
                  <img
                    className={css.relatedVenueImage}
                    src={venue.image.url}
                    alt=""
                    loading="lazy"
                  />
                ) : null}
                <span className={css.relatedVenueOverlay} aria-hidden="true" />
                <span className={css.relatedVenueText}>
                  <span>{venue.venueName}</span>
                  {[venue.city, venue.state].filter(Boolean).length ? (
                    <small>{[venue.city, venue.state].filter(Boolean).join(', ')}</small>
                  ) : null}
                </span>
              </a>
            ))}
          </div>
        </div>
      ) : null}
      {!relatedCards.length && related.length > 0 ? (
        <div className={css.relatedVenues}>
          <h3>Related venues</h3>
          <ul>
            {related.slice(0, 8).map(link => (
              <li key={link.linkId}>
                <a href={link.targetPath}>{link.anchorText}</a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {contextual.length > 0 ? (
        <div>
          <h3>{cityName ? `Explore more events in ${cityName}` : 'Explore more'}</h3>
          {(() => {
            const description = cityClusterDescription({ cityName, venueName, relatedVenues });
            return description ? <p>{description}</p> : null;
          })()}
          <ul>
            {contextual.map(link => (
              <li key={link.linkId}>
                <a href={link.targetPath}>{link.anchorText}</a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {performersList ? (
        <div>
          <h3>Performers</h3>
          {performersList}
        </div>
      ) : null}
    </div>
  );
};

SeoInternalLinks.propTypes = {
  links: array,
  relatedVenues: array,
  title: string,
  mode: oneOf(['all', 'guide', 'city', 'performers']),
  venueName: string,
  cityName: string,
};

export default SeoInternalLinks;
